import { useQuery } from "@tanstack/react-query";
import { fetchPatientSummary } from "../services/insights";
import { fetchExtendedPatientSummary } from "../services/api-config";
import { usePatientStore } from "../stores/patientStore";

export function usePatientSummary(patientId?: string) {
  const setSummary = usePatientStore((state) => state.setSummary);

  return useQuery({
    queryKey: ["patient-summary", patientId],
    queryFn: async ({ signal }) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }
      const response = await fetchPatientSummary(patientId, signal);
      if (response.summary) {
        setSummary(patientId, response.summary);
      }
      return response.summary;
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching extended patient summary with D1NAMO data counts
 */
export function useExtendedPatientSummary(patientId?: string) {
  return useQuery({
    queryKey: ["extended-patient-summary", patientId],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for extended patient summary");
      }

      const response = await fetchExtendedPatientSummary(patientId);
      return response.patient;
    },
    enabled: Boolean(patientId),
    staleTime: 10 * 60 * 1000, // 10 minutes - summary data changes less frequently
    retry: (failureCount, error) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = error.status as number;
        return status >= 500 && failureCount < 3;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook for fetching comprehensive patient insights including all D1NAMO data types
 */
export function useComprehensivePatientInsights(
  patientId?: string,
  options?: {
    includeGlucose?: boolean;
    includeInsulin?: boolean;
    includeECG?: boolean;
    includeMealImages?: boolean;
    startDate?: string;
    endDate?: string;
  },
) {
  return useQuery({
    queryKey: [
      "comprehensive-patient-insights",
      patientId,
      options?.includeGlucose,
      options?.includeInsulin,
      options?.includeECG,
      options?.includeMealImages,
      options?.startDate,
      options?.endDate,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for comprehensive insights");
      }

      const { fetchComprehensivePatientData } = await import(
        "../services/api-config"
      );
      const response = await fetchComprehensivePatientData(patientId, {
        includeGlucose: options?.includeGlucose ?? true,
        includeInsulin: options?.includeInsulin ?? true,
        includeECG: options?.includeECG ?? true,
        includeMealImages: options?.includeMealImages ?? true,
        startDate: options?.startDate,
        endDate: options?.endDate,
      });

      return response;
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      // Transform the comprehensive data into insights
      const insights = {
        patientProfile: data.patientProfile,
        summary: data.summary,
        glucoseInsights:
          data.glucoseMeasurements.length > 0
            ? {
                totalReadings: data.glucoseMeasurements.length,
                averageGlucose:
                  data.glucoseMeasurements.reduce(
                    (sum, reading) => sum + reading.valueMmol,
                    0,
                  ) / data.glucoseMeasurements.length,
                latestReading: data.glucoseMeasurements.sort(
                  (a, b) =>
                    new Date(b.recordedAt).getTime() -
                    new Date(a.recordedAt).getTime(),
                )[0],
                readingsByContext: data.glucoseMeasurements.reduce(
                  (acc, reading) => {
                    acc[reading.context] = (acc[reading.context] || 0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
              }
            : null,
        insulinInsights:
          data.insulinMeasurements.length > 0
            ? {
                totalMeasurements: data.insulinMeasurements.length,
                totalUnits: data.insulinMeasurements.reduce(
                  (sum, measurement) => sum + measurement.units,
                  0,
                ),
                averageUnitsPerDay:
                  data.insulinMeasurements.reduce(
                    (sum, measurement) => sum + measurement.units,
                    0,
                  ) / data.insulinMeasurements.length,
                insulinTypes: data.insulinMeasurements.reduce(
                  (acc, measurement) => {
                    acc[measurement.type] = (acc[measurement.type] || 0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
                injectionSites: data.insulinMeasurements.reduce(
                  (acc, measurement) => {
                    acc[measurement.injectionSite] =
                      (acc[measurement.injectionSite] || 0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
              }
            : null,
        ecgInsights:
          data.ecgFeatures.length > 0
            ? {
                totalRecordings: data.ecgFeatures.length,
                averageHeartRate: Math.round(
                  data.ecgFeatures.reduce(
                    (sum, feature) => sum + feature.heartRate,
                    0,
                  ) / data.ecgFeatures.length,
                ),
                averageHRV:
                  Math.round(
                    (data.ecgFeatures.reduce(
                      (sum, feature) =>
                        sum + feature.hrvFeatures.heartRateVariability,
                      0,
                    ) /
                      data.ecgFeatures.length) *
                      100,
                  ) / 100,
                rhythmClassifications: data.ecgFeatures.reduce(
                  (acc, feature) => {
                    acc[feature.waveformFeatures.rhythmClassification] =
                      (acc[feature.waveformFeatures.rhythmClassification] ||
                        0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
                arrhythmiaCount: data.ecgFeatures.filter(
                  (f) =>
                    f.waveformFeatures.rhythmClassification === "arrhythmia",
                ).length,
              }
            : null,
        mealInsights:
          data.mealEntries.length > 0
            ? {
                totalMeals: data.mealEntries.length,
                averageCalories:
                  data.mealEntries.filter((m) => m.calories !== null).length > 0
                    ? Math.round(
                        data.mealEntries
                          .filter((m) => m.calories !== null)
                          .reduce((sum, meal) => sum + meal.calories!, 0) /
                          data.mealEntries.filter((m) => m.calories !== null)
                            .length,
                      )
                    : 0,
                mealBalance: data.mealEntries.reduce(
                  (acc, meal) => {
                    if (meal.balance) {
                      acc[meal.balance] = (acc[meal.balance] || 0) + 1;
                    }
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
                mealQuality: data.mealEntries.reduce(
                  (acc, meal) => {
                    if (meal.quality) {
                      acc[meal.quality] = (acc[meal.quality] || 0) + 1;
                    }
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
              }
            : null,
        mealImageInsights:
          data.mealImages.length > 0
            ? {
                totalImages: data.mealImages.length,
                totalSizeMB:
                  Math.round(
                    (data.mealImages.reduce(
                      (sum, image) => sum + (image.metadata?.size || 0),
                      0,
                    ) /
                      (1024 * 1024)) *
                      100,
                  ) / 100,
                imagesWithDescriptions: data.mealImages.filter(
                  (img) => img.metadata?.description,
                ).length,
                formatDistribution: data.mealImages.reduce(
                  (acc, image) => {
                    const format = image.metadata?.format || "unknown";
                    acc[format] = (acc[format] || 0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
              }
            : null,
        generatedAt: data.generatedAt,
      };

      return insights;
    },
  });
}
